import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import { EmailBodyEditor } from '@lender/ckeditor-custom-build';
import { Box, Button, Popover, Typography, useTheme } from '@mui/material';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { MarkedAppFormsTreeFieldId } from 'app/services/autoLockForms/autoLockForms.fieldIds';
import PrismPagelessEditor from 'app/shared/components/forms/PrismPagelessEditor';
import useIsPrismPagelessEditorEnabled from 'app/shared/components/forms/PrismPagelessEditor/hooks/useIsPrismPagelessEditorEnabled';
import { PagelessToolbarPreset } from 'app/shared/components/forms/PrismPagelessEditor/PrismPagelessEditor.constants';
import PropType from 'prop-types';

import { useAsyncStateSafeStrictMode } from 'hooks/useAsyncStateSafeStrictMode';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoading from 'hooks/useLoading';
import usePopover from 'hooks/usePopover';
import { getScrollStyles } from 'styles/scrollbar.styles';

import { getEmailSnippetsAction } from 'store/companies/actions';
import { getRolesAction } from 'store/global/actions';
import { getGeneratedEmailSnippet } from 'store/loanApplications/actions';
import { selectFormLockedFieldsMap, selectFormLockedPage } from 'store/loanApplications/selectors';

import { ActivitiesTestIds } from '../../../Activities.testIds';
import { SNIPPETS_BUTTON_STYLES } from '../../EmailCreationModal.config';
import {
  EMAIL_BODY_ARIA_LABEL,
  LOAN_EMAIL_BODY_MIN_HEIGHT_PX,
} from '../../EmailCreationModal.constants';
import { EmailCreationGlobalStyles, getStyles } from '../../EmailCreationModal.styles';

import BodyAdditionalContent from './BodyAdditionalContent';
import EmailAttachments from './EmailAttachments';
import SnippetsPopover from './SnippetsPopover';

const snippetPopoverOptions = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

const lockId = 'editorIdToLock';
const defaultForwardedLinkedResources = [];

const NewEmailBody = ({
  isLoading,
  setIsLoading,
  form,
  validationErrors,
  setInForm,
  validateField,
  applicationId,
  repliedMessage,
  files,
  inReplyTo,
  setFiles,
  forwardedLinkedResources = defaultForwardedLinkedResources,
}) => {
  const theme = useTheme();
  const scrollClasses = getScrollStyles(theme);
  const classes = getStyles(theme);
  const isPrismPagelessEnabled = useIsPrismPagelessEditorEnabled();

  const bodySx = useMemo(
    () => [
      classes.bodyContainer,
      scrollClasses.scroll,
      isPrismPagelessEnabled && classes.prismBodyContainer,
    ],
    [
      classes.bodyContainer,
      scrollClasses.scroll,
      isPrismPagelessEnabled,
      classes.prismBodyContainer,
    ],
  );
  const bodyErrorSx = useMemo(() => [...bodySx, classes.bodyError], [bodySx, classes.bodyError]);
  const snippetButtonContainerSx = useMemo(
    () => [
      classes.snippetButtonContainer,
      isPrismPagelessEnabled && classes.prismSnippetButtonContainer,
    ],
    [isPrismPagelessEnabled, classes.snippetButtonContainer, classes.prismSnippetButtonContainer],
  );

  const dispatch = useDispatch();

  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.ACTIVITIES_EMAILS));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.ACTIVITIES_EMAILS));

  const isDisabledByLock = isLoading || isPageLocked;
  const isBodyDisabled = isDisabledByLock || !!lockedFieldsMap.body;

  const [toolbarContainer, setToolbarContainer] = useState(null);
  const prismEditorRef = useRef(null);
  const [editor, setEditor] = useAsyncStateSafeStrictMode(null);

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const [isLoadingSnippets, setIsLoadingSnippets] = useLoading();

  const onSelectSnippet = useCallback(
    e => {
      const snippetId = e.currentTarget.dataset.snippetId || '';

      setIsLoadingSnippets(true);
      setIsLoading(true);

      dispatch(getGeneratedEmailSnippet({ applicationId, snippetId }))
        .then(data => {
          if (data.content && isPrismPagelessEnabled) {
            prismEditorRef.current?.insertHtml(data.content, {
              locked: !data.isEditableAfterGeneration,
            });
            onClearAnchorElement();

            return;
          }

          if (data.content) {
            // <p/> adds extra line to put cursor and select snippet
            const securedContent = data.isEditableAfterGeneration
              ? data.content
              : `<p/><div contenteditable="false" style="user-select: none;">${data.content}</div></p>`;
            // editor.setData() does not add history step for undo
            const viewFragment = editor.data.processor.toView(securedContent);
            const modelFragment = editor.data.toModel(viewFragment);
            // insert content at cursor position
            editor.model.insertContent(modelFragment);
            onClearAnchorElement();
          }
        })
        .catch(Error)
        .finally(() => {
          setIsLoadingSnippets(false);
          setIsLoading(false);
        });
    },
    [
      applicationId,
      dispatch,
      setIsLoadingSnippets,
      onClearAnchorElement,
      setIsLoading,
      editor,
      isPrismPagelessEnabled,
    ],
  );

  useEffectSafeStrictMode(() => {
    setIsLoadingSnippets(true);
    dispatch(getEmailSnippetsAction({ filterByRole: true }))
      .catch(Error)
      .finally(() => setIsLoadingSnippets(false));
    dispatch(getRolesAction());
  }, [dispatch, setIsLoadingSnippets]);

  useEffect(() => {
    if (!editor || !toolbarContainer) {
      return;
    }

    toolbarContainer.appendChild(editor.ui.view.toolbar.element);
  }, [editor, toolbarContainer]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    if (isDisabledByLock || lockedFieldsMap.body) {
      editor.enableReadOnlyMode(lockId);

      return;
    }

    editor.disableReadOnlyMode(lockId);
  }, [isDisabledByLock, lockedFieldsMap.body, editor]);

  const onReady = useCallback(
    editor => {
      setEditor(editor);

      if (isLoading) {
        editor.enableReadOnlyMode(lockId);
      }
    },
    [setEditor, isLoading],
  );

  const onPrismBodyChange = useCallback(
    html => {
      setInForm('body', html);
      validateField('body', html);
    },
    [setInForm, validateField],
  );

  const onChangeInternalState = useCallback(
    (_, editor) => {
      const data = editor.getData();
      setInForm('body', data);
      validateField('body', data);
    },
    [setInForm, validateField],
  );

  return (
    <>
      <EmailCreationGlobalStyles />
      <Box className="flex" ref={setToolbarContainer} sx={classes.toolbar}>
        <Box sx={snippetButtonContainerSx}>
          <Button
            data-testid={ActivitiesTestIds.SNIPPETS_TOGGLE_BUTTON}
            disabled={isLoadingSnippets || isBodyDisabled}
            style={SNIPPETS_BUTTON_STYLES}
            sx={classes.snippetButton}
            type="button"
            onClick={onSetAnchorElement}
          >
            Snippets
          </Button>
        </Box>
      </Box>
      <Box
        className="px-12 overflow-y-auto grow flex flex-col"
        data-field-id={MarkedAppFormsTreeFieldId.ACTIVITIES_EMAIL_BODY}
        sx={validationErrors.body ? bodyErrorSx : bodySx}
      >
        {isPrismPagelessEnabled ? (
          <PrismPagelessEditor
            ariaLabel={EMAIL_BODY_ARIA_LABEL}
            data-testid={ActivitiesTestIds.EMAIL_BODY_EDITOR}
            disabled={isBodyDisabled}
            error={!!validationErrors.body}
            hasFrame={false}
            minHeight={LOAN_EMAIL_BODY_MIN_HEIGHT_PX}
            ref={prismEditorRef}
            toolbarContainer={toolbarContainer}
            toolbarPreset={PagelessToolbarPreset.EMAIL}
            value={form.body}
            onChange={onPrismBodyChange}
          />
        ) : (
          <CKEditor
            data={form.body}
            editor={EmailBodyEditor}
            onChange={onChangeInternalState}
            onReady={onReady}
          />
        )}

        {validationErrors.body && (
          <Typography className="mt-5" color="error" variant="regular12">
            {validationErrors.body}
          </Typography>
        )}

        <BodyAdditionalContent
          applicationId={applicationId}
          forwardedLinkedResources={forwardedLinkedResources}
          inReplyTo={inReplyTo}
          repliedMessage={repliedMessage}
          setIsLoading={setIsLoading}
        />

        <EmailAttachments files={files} setFiles={setFiles} />

        <Popover
          anchorEl={anchorEl}
          anchorOrigin={snippetPopoverOptions.anchorOrigin}
          open={!!anchorEl}
          transformOrigin={snippetPopoverOptions.transformOrigin}
          onClose={onClearAnchorElement}
        >
          <SnippetsPopover isLoading={isLoading} onSelectSnippet={onSelectSnippet} />
        </Popover>
      </Box>
    </>
  );
};

NewEmailBody.propTypes = {
  applicationId: PropType.string.isRequired,
  files: PropType.arrayOf(PropType.object).isRequired,
  form: PropType.object.isRequired,
  inReplyTo: PropType.string.isRequired,
  isLoading: PropType.bool.isRequired,
  repliedMessage: PropType.string.isRequired,
  setFiles: PropType.func.isRequired,
  setInForm: PropType.func.isRequired,
  setIsLoading: PropType.func.isRequired,
  validateField: PropType.func.isRequired,
  validationErrors: PropType.object.isRequired,
  forwardedLinkedResources: PropType.array,
};

export default memo(NewEmailBody);
